import { ChangeEvent, memo } from "react"

import { CellProps, useFocusManagement } from "components/TreeState"
import { ChangedPlanRecord, PlanRecordType } from "../types"
import { checkBuyingStatus } from "utils/type-guards"
import { SupplierProductTuple, SupplierTuple } from "models/supplier"
import { css, cx } from "@emotion/css"
import {
  formatFixedNumberCurrency,
  formatFloatingPointNumber,
} from "utils/number-format"
import { isSemanticReadonly, usePlanCachedData } from "../utils"
import { queryCache, useQuery } from "react-query"

import Autocomplete from "@material-ui/lab/Autocomplete"
import { CellError } from "./CellError"
import CloseIcon from "@material-ui/icons/Close"
import { Commission } from "models/commissions"
import { FieldsKey } from "../components/AutoCompute/types"
import { MediaType } from "routes/Base/Admin/GlobalMediaTypes/types"
import { Paginated } from "api/types"
import { Tooltip } from "@material-ui/core"
import { fetchMediaPlanAvailableCommissions } from "api/commissions"
import { get } from "lodash"
import { set } from "lodash/fp"
import { tableAggregations } from "../components/AutoCompute/useAggregations"
import { useAtomValue } from "jotai/utils"
import { useAutoCompute } from "../components/AutoCompute/useAutoCompute"
import { useAutocomputeValidation } from "../useAutocomputeValidation"
import { useBillingTypesRules } from "../useBillingTypeRules"
import { useI18n } from "providers/I18n"
import { useParams } from "react-router-dom"
import { useSnapshot } from "valtio"
import { useTableSource } from "../TableStateProvider"
import { useUndoStack } from "../useUndoStack"
import { DEFAULT_PRECISION, usePrecision } from "providers/A12n"
import { roundHalfEven } from "../components/AutoCompute/utils"

export const PreciseCommisionCell = memo(function _PreciseCommisionCell(
  props: CellProps,
) {
  const precision = usePrecision()
  return <CommisionCell {...props} precision={precision} />
})

export const CommisionCell = memo(function _CommisionCell(props: CellProps) {
  const {
    rowId: id,
    dataKey,
    columnId,
    semantic,
    disabled,
    primaryKey,
    precision = DEFAULT_PRECISION,
  } = props
  const { id: planId } = useParams<{ id: string }>()
  const { t } = useI18n()
  const autoCompute = useAutoCompute()
  const aggregations = useAtomValue(tableAggregations)
  const tableSource = useTableSource()
  const readFrom = useSnapshot(tableSource.state)
  const readError = useSnapshot(tableSource.errors)
  const globalUndoState = useUndoStack<ChangedPlanRecord>()
  const writeTo = tableSource.state

  const action = readFrom[id]?.action
  const type = readFrom[id]?.type as PlanRecordType
  const packaged = readFrom[id]?.packaged
  const updatable = !action || action === "UPDATE_ORDER"
  const error = get(readError, `${id}.${columnId}`)
  const row = get(readFrom, `${id}`) as ChangedPlanRecord
  const isBooking = primaryKey === "buyingMediaFields"
  const isMediaSubmedia =
    type === "media_type_plan" || type === "media_subtype_plan"
  const planData = usePlanCachedData(isBooking)

  const value = get(row, `${dataKey}.value`)
  const aggregatedValue = get(aggregations, [id, columnId])
  const hasAggregation = !!Object.keys(aggregations).includes(id)
  let appliedValue =
    isMediaSubmedia && hasAggregation && !isBooking && !packaged
      ? (aggregatedValue ?? value)
      : value

  const forcedNumeric = !isNaN(Number(appliedValue))
  appliedValue = forcedNumeric
    ? roundHalfEven(Number(appliedValue)).toString()
    : appliedValue

  const fvalue = formatFloatingPointNumber(precision, 2)(appliedValue)

  const commissionId = get(row, `${dataKey}.commission.id`) as string

  const buyingStatusVal = String(get(readFrom, `${id}.buyingStatus`))
  const buyingStatus = checkBuyingStatus(buyingStatusVal) ? buyingStatusVal : ""

  const { handleFocus, hasChanged } = useAutocomputeValidation(commissionId)

  const isEditable = !isSemanticReadonly(
    type,
    primaryKey as FieldsKey,
    semantic,
    readFrom[id]?.packaged,
  )
  const isDisabled =
    disabled || !isEditable || buyingStatus === "POSTED_FOR_BILLING"

  const mtps = new URLSearchParams([
    ["configScope", "MEDIA_PLAN"],
    ["configOwnerId", planId],
  ])

  const mediaTypes = queryCache.getQueryData<Paginated<MediaType>>([
    "media-types",
    mtps.toString(),
  ])

  const media = mediaTypes?.data?.find((mt) => mt.id === row?.ancestorMediaId)
  const params = getCommissionsParams(
    row,
    primaryKey as FieldsKey,
    media,
    semantic as "mediaCommissions" | "agencyCommissions",
  )

  const minRequired = !!semantic && !!params.get("mediaTypeSemanticCode")
  const extraRequired =
    semantic === "mediaCommissions" ? !!params.get("supplierId") : true
  const enabled = minRequired && extraRequired

  const cached = queryCache.getQueryData<Commission[]>([
    "media-plan-commissions",
    planId,
    params.toString(),
  ])

  const { data: availableCommissions } = useQuery(
    ["media-plan-commissions", planId, params.toString()],
    fetchMediaPlanAvailableCommissions,
    {
      initialData: cached,
      enabled: !cached && enabled,
      refetchOnMount: !cached,
    },
  )

  const commission = availableCommissions?.find((c) => c.id === commissionId)
  const options = availableCommissions?.length
    ? [null, ...availableCommissions]
    : []

  const handleChange = (_event: ChangeEvent<{}>, cvalue: Commission | null) => {
    globalUndoState.push([writeTo[id] as ChangedPlanRecord])
    const writeValue = cvalue
      ? { commission: { id: cvalue.id } }
      : { commission: null, value: 0 }

    writeTo[id] = set(`${dataKey}`, writeValue, writeTo[id])
    tableSource.clearError(id)
    updatable && (writeTo[id] = set("action", "UPDATE", writeTo[id]))
  }

  const handleBlur = () => {
    hasChanged(commissionId) &&
      autoCompute?.compute(writeTo[id] as ChangedPlanRecord)
  }
  const { getInputProps } = useFocusManagement()
  const ip = getInputProps()
  const canPick = row?.type === "line_item" || row?.type === "package"

  // apply billing type rules
  const viewable = useBillingTypesRules(row, primaryKey, semantic)
  if (!viewable) return <span />

  return canPick ? (
    <div className={styles.tworow}>
      <div>
        <Autocomplete
          disabled={isDisabled}
          classes={{
            option: styles.option,
            endAdornment: styles.hidden,
          }}
          getOptionLabel={(opt) => opt?.label || ""}
          value={commission}
          onFocus={handleFocus}
          onChange={handleChange}
          options={options}
          onKeyDown={ip.onKeyDown}
          filterSelectedOptions
          getOptionSelected={(opt, val) => opt?.id === val?.id}
          popupIcon={<span />}
          style={{
            width: "inherit",
            display: "flex",
          }}
          onBlur={handleBlur}
          renderOption={(option) =>
            !!option ? (
              <>
                <span>{option.amount}%</span>
                <span className="BodyBody-2Dark-greyRegular">
                  ({t(`commission.computedOn.${option.computedOn}`)})
                </span>
                <span className="BodyBody-2Dark-greyRegular">
                  {option.label}
                </span>
              </>
            ) : (
              <>
                <span />
                <span>
                  <CloseIcon
                    data-testid="clear-commission"
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      handleChange(e, null)
                    }}
                  />
                </span>
              </>
            )
          }
          renderInput={(params) => {
            return (
              <Tooltip
                title={
                  !!commission?.id
                    ? `${t(
                        `commission.computedOn.${commission?.computedOn}`,
                      )} ${commission?.amount}%`
                    : ""
                }
                placement="bottom-start"
              >
                <div
                  ref={params.InputProps.ref}
                  id={params.id}
                  className={cx(styles.info, {
                    [styles.readonly]: isDisabled && !commission,
                    [styles.noPercentage]: !commission?.id,
                  })}
                >
                  {!!commission?.id ? (
                    <div
                      className={cx(
                        "commission",
                        "BodyBody-xsDark-greyRegular",
                      )}
                    >
                      <span className="label">
                        {t(`commission.computedOn.${commission?.computedOn}`)}
                      </span>
                      <span
                        className={cx("BodyBody-2Dark-greyRegular", "amount")}
                      >
                        {commission?.amount}%
                      </span>
                    </div>
                  ) : null}
                  <div className="value">
                    {!!fvalue
                      ? formatFixedNumberCurrency(
                          fvalue,
                          planData?.currencyCode,
                          precision,
                        )
                      : ""}
                  </div>
                  <input {...params.inputProps} />
                </div>
              </Tooltip>
            )
          }}
        />
        {error ? <CellError error={error} /> : null}
      </div>
    </div>
  ) : (
    <span className={styles.readonly}>
      {!!fvalue
        ? formatFixedNumberCurrency(fvalue, planData?.currencyCode, precision)
        : null}
    </span>
  )
})

export function getCommissionsParams(
  li: ChangedPlanRecord,
  fieldsKey: FieldsKey = "planningMediaFields",
  media?: MediaType,
  semantic?: "agencyCommissions" | "mediaCommissions",
) {
  const params = new URLSearchParams()

  const mediaTypeSemanticCode = li?.ancestorMediaSemanticCode
  const mediaSubtypeSemanticCode = li?.mediaSemanticCode
  const { vendor: vendorFieldId, vendorProduct: vendorProductFieldId } =
    media?.defaultFieldIdBySemantic || {}

  const vendor = get(li, `${fieldsKey}.${vendorFieldId}`) as SupplierTuple
  const vendorProduct = get(
    li,
    `${fieldsKey}.${vendorProductFieldId}`,
  ) as SupplierProductTuple
  const commissionSemantic = semantic === "agencyCommissions" ? "AC" : "MC"

  !!commissionSemantic && params.set("commissionSemantic", commissionSemantic)
  !!vendor && params.set("supplierId", vendor?.mediaOwnerId)
  !!vendorProduct &&
    params.set("supplierProductId", vendorProduct.supplierProductId)
  !!mediaTypeSemanticCode &&
    params.set("mediaTypeSemanticCode", mediaTypeSemanticCode)
  !!mediaSubtypeSemanticCode &&
    params.set("mediaSubtypeSemanticCode", mediaSubtypeSemanticCode)

  return params
}

const styles = {
  tworow: css`
    display: flex;
    flex-direction: column;
    width: 100%;
  `,
  relative: css`
    position: relative;
  `,
  option: css`
    font-size: 13px;
    display: grid;
    grid-auto-flow: column;
    grid-template-columns: 35px 48px auto;
    grid-column-gap: var(--spacing-xs);
    > span {
      overflow: hidden;
      max-width: 100%;
      white-space: nowrap;
      text-overflow: ellipsis;
      justify-self: flex-end;
    }
  `,
  info: css`
    width: 100%;
    font-size: 13px;
    font-weight: inherit;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    padding: 2px 0;
    .title {
      min-width: 54px;
    }
    .value {
      height: 100%;
    }
    .label {
      text-overflow: ellipsis;
      white-space: nowrap;
      overflow: hidden;
      padding-right: var(--spacing-xxs);
      max-width: 56px;
    }
    .amount {
      padding-right: var(--spacing-xxs);
    }
    .commission {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: var(--spacing-xxs);
    }
    input {
      width: 100%;
      height: 100%;
      position: absolute;
      border-color: transparent;
      background-color: transparent;
      color: transparent;
      &:hover,
      &:focus,
      &::selection,
      &:active {
        border-color: transparent;
        background-color: transparent;
        color: transparent;
        outline: none;
      }
    }
  `,
  hidden: css`
    display: none;
  `,
  readonly: css`
    width: 100%;
    text-align: end;
    justify-content: flex-end;
  `,
  noPercentage: css`
    justify-content: flex-end;
  `,
}
